import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { fmtPrice } from "../lib/i18n";
import { Minus, Plus, ShoppingBag, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, lang, addToCart } = useApp();
    const [product, setProduct] = useState(null);
    const [variant, setVariant] = useState(null);
    const [qty, setQty] = useState(1);
    const [mainImg, setMainImg] = useState("");

    useEffect(() => {
        api.get(`/products/${slug}`).then((r) => {
            setProduct(r.data);
            const firstVariant = r.data.variants && r.data.variants.length > 0 ? r.data.variants[0] : null;
            setVariant(firstVariant);
            setMainImg(firstVariant?.image || r.data.image);
        }).catch(() => navigate("/products"));
    }, [slug, navigate]);

    if (!product) {
        return <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10 text-white/60">
            {lang === "ar" ? "جاري التحميل..." : "Loading..."}
        </div>;
    }

    const name = lang === "ar" ? product.name_ar : product.name_en;
    const description = lang === "ar" ? product.description_ar : product.description_en;
    const category = lang === "ar" ? product.category_ar : product.category_en;
    const price = variant ? variant.price : product.base_price || 0;
    const hasVariants = product.variants && product.variants.length > 0;
    const variantsHaveImages = hasVariants && product.variants.some((v) => v.image);
    const isQuoteOnly = product.quote_only;
    const isComingSoon = product.coming_soon;

    const selectVariant = (v) => {
        setVariant(v);
        if (v.image) setMainImg(v.image);
    };

    const handleAdd = () => {
        addToCart({
            product_id: product.id,
            product_name: name,
            variant_label: variant ? variant.label : null,
            price,
            quantity: qty,
            image: (variant && variant.image) || product.image,
        });
        toast.success(lang === "ar" ? "تمت الإضافة للسلة" : "Added to cart");
    };

    return (
        <div data-testid="product-detail-page" className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
            <Link
                to="/products"
                data-testid="back-to-products"
                className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-8 text-sm tracking-wider"
            >
                {lang === "ar" ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                <span>{lang === "ar" ? "رجوع إلى المنتجات" : "Back to Products"}</span>
            </Link>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
                {/* Gallery */}
                <div>
                    <div className="aspect-square bg-[#141414] border border-[#D4AF37]/30 overflow-hidden">
                        <img src={mainImg} alt={name} className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535016120720-40c646be5580"; }} />
                    </div>
                    {product.gallery && product.gallery.length > 1 && (
                        <div className="grid grid-cols-4 gap-3 mt-4">
                            {product.gallery.map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setMainImg(img)}
                                    data-testid={`gallery-thumb-${idx}`}
                                    className={`aspect-square border overflow-hidden ${mainImg === img ? 'border-[#D4AF37]' : 'border-[#D4AF37]/20'}`}
                                >
                                    <img src={img} alt={`${name}-${idx}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="lg:pt-8">
                    <div className="overline mb-3">{category}</div>
                    <h1 data-testid="product-name" className="section-heading text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-[1.05]">
                        {name}
                    </h1>
                    {isComingSoon ? (
                        <p data-testid="product-price" className="price-display text-2xl text-[#D4AF37] mb-8">
                            {lang === "ar" ? "قريباً" : "Coming Soon"}
                        </p>
                    ) : isQuoteOnly ? (
                        <p data-testid="product-price" className="price-display text-2xl text-[#D4AF37] mb-8">
                            {lang === "ar" ? "السعر حسب الطلب" : "Price on Request"}
                        </p>
                    ) : (
                        <div className="mb-8">
                            <div className="flex items-baseline gap-4 flex-wrap">
                                <p data-testid="product-price" className="price-display text-3xl text-[#D4AF37]">{fmtPrice(price, lang)}</p>
                                <p data-testid="product-original-price" className="price-display text-xl text-white/40 line-through decoration-white/40">
                                    {fmtPrice(Math.round(price * 1.25), lang)}
                                </p>
                                <span data-testid="product-discount-badge" className="text-xs bg-[#D4AF37] !text-black px-3 py-1 font-bold tracking-wider">
                                    {lang === "ar" ? "خصم 25٪" : "-25% OFF"}
                                </span>
                            </div>
                            <p className="mt-2 text-sm text-white/60 tracking-wide">
                                {lang === "ar" ? "عرض لفترة محدودة" : "Limited time offer"}
                            </p>
                        </div>
                    )}
                    <div className="hairline w-24 mb-8" />

                    <p className="text-white/70 text-base leading-relaxed mb-10">{description}</p>

                    {product.category_ar === "خيام" && (
                        <div data-testid="tent-includes-box" className="mb-10 border border-[#D4AF37]/30 bg-[#141414]/60 p-5">
                            <div className="text-[#D4AF37] text-sm font-semibold tracking-wider mb-3">
                                {lang === "ar" ? "السعر شامل:" : "Price includes:"}
                            </div>
                            <ul className="space-y-2 text-white/80 text-sm leading-relaxed">
                                {(lang === "ar"
                                    ? ["الإضاءة", "التركيب", "الجلسة", "الفرش", "الكهرباء"]
                                    : ["Lighting", "Installation", "Seating", "Furnishing", "Electricity"]
                                ).map((item) => (
                                    <li key={item} className="flex items-center gap-2">
                                        <span className="text-[#D4AF37]">✦</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {hasVariants && !isQuoteOnly && !isComingSoon && (
                        <div className="mb-8">
                            <div className="overline mb-4">
                                {variantsHaveImages ? (lang === "ar" ? "التصميم" : "Design") : t.product.size}
                            </div>
                            {variantsHaveImages ? (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {product.variants.map((v) => (
                                        <button
                                            key={v.label}
                                            onClick={() => selectVariant(v)}
                                            data-testid={`variant-${v.label}`}
                                            className={`group border overflow-hidden transition-all ${
                                                variant?.label === v.label
                                                    ? "border-[#D4AF37]"
                                                    : "border-[#D4AF37]/20 hover:border-[#D4AF37]/60"
                                            }`}
                                        >
                                            <div className="aspect-square overflow-hidden">
                                                <img src={v.image} alt={v.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                            </div>
                                            <div className={`p-2 text-xs text-center ${variant?.label === v.label ? "bg-[#D4AF37] text-black" : "bg-[#141414] text-white"}`}>
                                                {v.label}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-3">
                                    {product.variants.map((v) => (
                                        <button
                                            key={v.label}
                                            onClick={() => selectVariant(v)}
                                            data-testid={`variant-${v.label}`}
                                            className={`px-5 py-3 border text-sm tracking-widest transition-all ${
                                                variant?.label === v.label
                                                    ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                                                    : "border-[#D4AF37]/30 text-white hover:border-[#D4AF37]"
                                            }`}
                                        >
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!isQuoteOnly && !isComingSoon && (
                        <div className="mb-10">
                            <div className="overline mb-4">{t.product.qty}</div>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setQty(Math.max(1, qty - 1))}
                                    data-testid="qty-decrease"
                                    className="w-11 h-11 border border-[#D4AF37]/30 text-white flex items-center justify-center hover:border-[#D4AF37]"
                                >
                                    <Minus size={14} />
                                </button>
                                <span data-testid="qty-value" className="text-white text-lg w-10 text-center">{qty}</span>
                                <button
                                    onClick={() => setQty(qty + 1)}
                                    data-testid="qty-increase"
                                    className="w-11 h-11 border border-[#D4AF37]/30 text-white flex items-center justify-center hover:border-[#D4AF37]"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>
                    )}

                    {isComingSoon ? (
                        <button
                            disabled
                            data-testid="coming-soon-btn"
                            className="btn-gold w-full md:w-auto opacity-60 cursor-not-allowed"
                        >
                            ⏳ {lang === "ar" ? "قريباً — ترقّبوا" : "Coming Soon — Stay Tuned"}
                        </button>
                    ) : isQuoteOnly ? (
                        <a
                            href={`https://wa.me/971524110193?text=${encodeURIComponent(
                                lang === "ar"
                                    ? `السلام عليكم، أبغى عرض سعر لـ "${name}" من موقع سراء تنت.\nالقياس المطلوب: \nعدد الجلسات: \nملاحظات: `
                                    : `Hi, I'd like a custom quote for "${name}" from SRAA TENT.\nDimensions: \nNumber of seats: \nNotes: `
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="quote-whatsapp-btn"
                            className="btn-gold btn-gold-filled w-full md:w-auto"
                        >
                            📱 {lang === "ar" ? "اطلب عرض سعر عبر واتساب" : "Request Quote via WhatsApp"}
                        </a>
                    ) : (
                        <button onClick={handleAdd} data-testid="add-to-cart-btn" className="btn-gold btn-gold-filled w-full md:w-auto">
                            <ShoppingBag size={16} /> {t.product.add_to_cart}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
